import React, { useState, useCallback } from 'react'; // 🛑 Import useState and useCallback
import { Typography, Stack, Box } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'; // Removed Legend import
import EmptyPieChart from './EmptyPieChart';

interface DonutCardProps {
  title: string;
  data: Array<{ name: string; value: number }>;
  colors: string[];
  cardWidth?: number;
  chartHeight?: number;
}

// --- Custom Interactive Legend Component ---

interface CustomLegendProps {
  data: Array<{ name: string; value: number }>;
  colors: string[];
  hiddenSegments: Record<string, boolean>;
  toggleSegment: (name: string) => void;
}

const CustomLegend: React.FC<CustomLegendProps> = ({ data, colors, hiddenSegments, toggleSegment }) => (
  <Stack
    direction="row"
    spacing={2}
    sx={{
      flexWrap: 'wrap',
      justifyContent: 'center',
      rowGap: 1,
    }}
  >
    {data.map((entry, idx) => {
      const isHidden = hiddenSegments[entry.name];
      return (
        <Stack
          key={entry.name}
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => toggleSegment(entry.name)} // 🛑 Click handler
          sx={{
            cursor: 'pointer',
            opacity: isHidden ? 0.5 : 1, // Dim if hidden
            userSelect: 'none',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: colors[idx],
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              lineHeight: 1,
              fontSize: 10,
              p: 0,
              m: 2,
              textDecoration: isHidden ? 'line-through' : 'none',
            }}
          >
            {/* {entry.name.replace(/client/gi, '').replace(/dm/gi, '')} */}
            {entry.name.replace(/^client\s*/i, '').replace(/^dm\s*/i, '')}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const DonutCard = ({ title, data, colors, cardWidth, chartHeight = 180 }: DonutCardProps) => {
  // 🛑 1. State to track hidden segments
  // Key: Segment name, Value: boolean (true if hidden)
  const [hiddenSegments, setHiddenSegments] = useState<Record<string, boolean>>({});

  // 🛑 2. Handler to toggle segment visibility
  const toggleSegment = useCallback((name: string) => {
    setHiddenSegments((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  }, []);

  // 🛑 3. Filter data for rendering
  const visibleData = data.filter((d) => !hiddenSegments[d.name]);

  const visibleColors = colors.filter((_, index) => {
    const name = data[index]?.name;
    return name ? !hiddenSegments[name] : false;
  });

  const total = data?.reduce((acc, d) => acc + d.value, 0);

  function isColorLight(hex: string) {
    // Remove '#' if present
    hex = hex.replace('#', '');
    // Parse r, g, b values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Perceived brightness
    return r * 0.299 + g * 0.587 + b * 0.114 > 186;
  }

  return (
    <>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      {!data || total === 0 ? (
        <EmptyPieChart height={chartHeight} data={data} colors={colors} />
      ) : (
        <Stack spacing={1} alignItems="center">
          <ResponsiveContainer width={cardWidth || 540} height={chartHeight}>
            <PieChart>
              <Pie
                dataKey="value"
                isAnimationActive={false}
                data={visibleData} // 🛑 Use visibleData
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={75}
                stroke="none"
                /* 1. minAngle ensures 1% segments are wide enough to click/see */
                minAngle={15}
                /* 2. labelLine connects the text to the slice */
                labelLine={false}
                startAngle={180}
                endAngle={-180}
                // ...existing code...
                label={({ cx, cy, midAngle, innerRadius, outerRadius, value, index }) => {
                  const visibleTotal = visibleData.reduce((acc, d) => acc + d.value, 0);
                  if (!visibleTotal) return null;
                  const percent = (value / visibleTotal) * 100;
                  // if (percent === 0) return null;
                  const RADIAN = Math.PI / 180;
                  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  const color = colors[index] || '#000';
                  const textColor = isColorLight(color) ? '#222' : '#fff';
                  return (
                    <text
                      x={x}
                      y={y}
                      fill={textColor}
                      // textAnchor={x > cx ? 'start' : 'end'}
                      // dominantBaseline="center"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="10px"
                      fontWeight="bold"
                    >
                      {`${percent.toFixed(0)}%`}
                    </text>
                  );
                }}
                // ...existing code...
              >
                {visibleColors.map((entry: string) => (
                  <Cell key={entry} fill={entry} />
                ))}
              </Pie>
              <Tooltip
                wrapperStyle={{ zIndex: 10000 }}
                contentStyle={{ fontSize: 10, padding: '4px 6px', borderRadius: 6 }}
                // Optional: Custom Tooltip formatter to show the actual total value,
                // not just the visible total value percentage.
              />
            </PieChart>
          </ResponsiveContainer>

          {/* 🛑 Use the CustomLegend component */}
          <CustomLegend data={data} colors={colors} hiddenSegments={hiddenSegments} toggleSegment={toggleSegment} />
        </Stack>
      )}
    </>
  );
};

export default DonutCard;
